/* ============================================================
 * frontend-v3/src/components/tags/TagEditor.tsx
 *
 * NMW-410 — reusable tag editor: chips (removable) + a
 * <TagAutocompleteInput> add row. Two modes, one shared view:
 *
 *   Controlled ({ value, onChange }) — used by EditMetadata; the parent
 *     owns the tag list and persists it on Save.
 *
 *   Self-persisting ({ assetId }) — used by ContentDetail; seeds from the
 *     asset's typed tags (`api.assetQueries.getAssetTags`, so `kind`
 *     survives) and persists every add/remove immediately through the
 *     `api.mutations.assets.applyAssetTags` sole-writer path via
 *     `useMutationWithToast`. On a backend throw the wrapper surfaces the
 *     error toast (honest-state) and the optimistic edit is rolled back.
 *
 * Dedup identity is `(kind, normalizedTag)`, mirroring the backend
 * `assetTags` projection so a controlled and a self-persisting editor add
 * the same way.
 * ============================================================ */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import type { Id } from '@convex/dataModel';
import { Btn, CircaTag, Skeleton } from '../primitives';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import {
  TagAutocompleteInput,
  TAG_KIND_LABEL,
  type TagEntry,
  type TagKind,
} from './TagAutocompleteInput';

export type { TagEntry, TagKind } from './TagAutocompleteInput';

function norm(s: string): string {
  return s.trim().toLowerCase();
}
function keyOf(e: TagEntry): string {
  return `${e.kind}::${norm(e.tag)}`;
}
function dedupe(entries: TagEntry[]): TagEntry[] {
  const seen = new Set<string>();
  const out: TagEntry[] = [];
  for (const e of entries) {
    const tag = e.tag.trim();
    if (!tag) continue;
    const k = keyOf({ tag, kind: e.kind });
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ tag, kind: e.kind });
  }
  return out;
}

/** Presentational chips + add-row. Owns only the kind Select's value. */
function TagEditorView({
  entries,
  onAdd,
  onRemove,
  busy,
  loading = false,
  emptyLabel = 'Sin etiquetas',
}: {
  entries: TagEntry[];
  onAdd: (e: TagEntry) => void;
  onRemove: (e: TagEntry) => void;
  busy?: boolean;
  /** Honest-state: chips are still loading from the server (undefined) — show
   *  a skeleton, NOT the empty-state label. */
  loading?: boolean;
  emptyLabel?: string;
}) {
  const [kind, setKind] = useState<TagKind>('freeform');
  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <TagAutocompleteInput kind={kind} onKindChange={setKind} onPick={onAdd} />
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', opacity: busy ? 0.6 : 1 }}>
        {loading ? (
          <>
            <Skeleton w={72} h={24} r={999} aria-busy="true" />
            <Skeleton w={96} h={24} r={999} aria-busy="true" />
            <Skeleton w={60} h={24} r={999} aria-busy="true" />
          </>
        ) : entries.length === 0 ? (
          <span style={{ fontSize: 12, color: 'var(--muted-slate)' }}>{emptyLabel}</span>
        ) : (
          entries.map((e, i) => (
            <CircaTag key={`${keyOf(e)}:${i}`} onRemove={() => onRemove(e)}>
              {e.kind === 'freeform' ? e.tag : `${TAG_KIND_LABEL[e.kind]}: ${e.tag}`}
            </CircaTag>
          ))
        )}
      </div>
    </div>
  );
}

/** Controlled: the parent owns the list; add/remove lift through onChange. */
function ControlledTagEditor({
  value,
  onChange,
}: {
  value: TagEntry[];
  onChange: (next: TagEntry[]) => void;
}) {
  return (
    <TagEditorView
      entries={value}
      onAdd={(e) => onChange(dedupe([...value, e]))}
      onRemove={(e) => onChange(value.filter((x) => keyOf(x) !== keyOf(e)))}
    />
  );
}

/** Self-persisting: seeds from the asset's typed tags and writes each edit. */
function SelfPersistTagEditor({ assetId }: { assetId: string }) {
  const server = useQuery(
    api.assetQueries.getAssetTags,
    assetId ? { assetId: assetId as unknown as Id<'assets'> } : 'skip',
  ) as Array<{ tag: string; kind: TagKind; normalizedTag: string }> | undefined;

  const [entries, setEntries] = useState<TagEntry[]>([]);
  // Track the last server snapshot so a fresh server push re-seeds local
  // state, but a purely-local optimistic edit is not clobbered mid-flight.
  const seededRef = useRef<string | null>(null);
  useEffect(() => {
    if (server === undefined) return;
    const snapshot = JSON.stringify(server.map((r) => `${r.kind}::${r.normalizedTag}`));
    if (seededRef.current === snapshot) return;
    seededRef.current = snapshot;
    setEntries(server.map((r) => ({ tag: r.tag, kind: r.kind })));
  }, [server]);

  const { run: applyAssetTags, loading } = useMutationWithToast(
    api.mutations.assets.applyAssetTags,
    {
      logLabel: 'content-detail-tags',
      mutationName: 'assets.applyAssetTags',
      successMessage: 'Etiquetas actualizadas',
    },
  );

  async function persist(next: TagEntry[], prev: TagEntry[]) {
    setEntries(next);
    try {
      await applyAssetTags({
        assetId: assetId as unknown as Id<'assets'>,
        tags: next.map((e) => ({ tag: e.tag, kind: e.kind })),
      });
      // Reflect the just-written set as the seeded baseline so the incoming
      // server push (same content) does not re-trigger a reseed.
      seededRef.current = JSON.stringify(next.map((e) => `${e.kind}::${norm(e.tag)}`));
    } catch {
      // Wrapper already surfaced the error toast — roll back the optimism.
      setEntries(prev);
    }
  }

  return (
    <TagEditorView
      entries={entries}
      busy={loading}
      loading={server === undefined}
      emptyLabel="Sin etiquetas. Añade la primera aquí."
      onAdd={(e) => {
        const next = dedupe([...entries, e]);
        if (next.length === entries.length) return; // dup — no write
        void persist(next, entries);
      }}
      onRemove={(e) => {
        const next = entries.filter((x) => keyOf(x) !== keyOf(e));
        void persist(next, entries);
      }}
    />
  );
}

export function TagEditor(
  props:
    | { assetId: string; value?: never; onChange?: never }
    | { assetId?: undefined; value: TagEntry[]; onChange: (next: TagEntry[]) => void },
) {
  if (props.assetId) {
    return <SelfPersistTagEditor assetId={props.assetId} />;
  }
  return <ControlledTagEditor value={props.value} onChange={props.onChange} />;
}

export default TagEditor;
